import { z } from 'zod';

/**
 * Schema for get_public_latest_case RPC response.
 * This schema serves as a CONTRACT between the database and frontend.
 *
 * BREAKING CHANGES to this schema require updating:
 * 1. The SQL function: supabase/migrations/get_public_latest_case.sql
 * 2. The TypeScript interface: types/database-extensions.ts
 * 3. The component: pages/public/HomePage.tsx (LatestCasePreview)
 *
 * Fields that caused regressions when missing:
 * - publicatie_datum: Homepage shows "Gepubliceerd op X"
 * - themas: Homepage shows thema badges
 * - wetsartikelen: Homepage shows wetsartikel badges
 */
export const PublicLatestCaseSchema = z.object({
  id: z.string().min(1),
  ecli: z.string(),
  titel: z.string(),
  samenvatting: z.string(),
  uitspraak_datum: z.string(), // DATE as ISO string (YYYY-MM-DD)
  publicatie_datum: z.string(), // DATE as ISO string - CRITICAL: was missing in buggy migration
  instantie_naam: z.string().nullable(),
  rechtsgebieden: z.array(z.string()),
  themas: z.array(z.string()), // CRITICAL: was missing in buggy migration
  wetsartikelen: z.array(z.string()), // CRITICAL: was missing in buggy migration
});

export type PublicLatestCase = z.infer<typeof PublicLatestCaseSchema>;

/**
 * Validates that the actual RPC response matches the expected schema.
 * Throws a descriptive error if validation fails.
 */
export function validatePublicLatestCase(data: unknown): PublicLatestCase | null {
  if (data === null || data === undefined) return null;
  return PublicLatestCaseSchema.parse(data);
}

/**
 * Safe parse that returns success/error instead of throwing.
 * Useful for tests and error handling.
 */
export function safeParsePublicLatestCase(data: unknown) {
  if (data === null || data === undefined) {
    return { success: true as const, data: null };
  }
  return PublicLatestCaseSchema.safeParse(data);
}
